import React, { useMemo } from "react";
import apiConfig from "../../config/apiConfig";
import { AllCommunityModule, ModuleRegistry, ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useQuery } from "@tanstack/react-query";
import { authFetch } from "../../apis/authFetch";

ModuleRegistry.registerModules([AllCommunityModule]);

type Student = {
  id: string;
  name?: string;
  email?: string;
  roll_no?: string;
  batch?: string;
};

const getBatchLabel = (b: any) =>
  b?.batch_name || b?.batchName || b?.name || b?.id || "";

const prettifyHeader = (str: string) =>
  (str || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const ReadStudent = () => {
  const studentQuery = useQuery({
    queryKey: ["StudentList"],
    queryFn: async () => {
      const params = new URLSearchParams({ queryId: "GET_ALL" });

      const res = await authFetch(
        `${apiConfig.getResourceUrl("student")}?${params.toString()}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );

      if (!res.ok) throw new Error("Failed to load students");

      const json = await res.json();
      return (json.resource || []) as Student[];
    },
  });

  const batchQuery = useQuery({
    queryKey: ["BatchList"],
    queryFn: async () => {
      const params = new URLSearchParams({ queryId: "GET_ALL" });

      const res = await authFetch(
        `${apiConfig.getResourceUrl("batch")}?${params.toString()}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );

      if (!res.ok) throw new Error("Failed to load batches");

      const json = await res.json();
      return (json.resource || []);
    },
  });

  const batchMap = useMemo(() => {
    const map: Record<string, string> = {};
    (batchQuery.data || []).forEach((b: any) => {
      if (b?.id) map[b.id] = getBatchLabel(b);
    });
    return map;
  }, [batchQuery.data]);

  const rowData = useMemo(() => {
    const students = studentQuery.data || [];
    return students.map((s) => ({
      ...s,
      batch_name: batchMap[s.batch || ""] || s.batch || "-",
    }));
  }, [studentQuery.data, batchMap]);

  const colDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: prettifyHeader("name"),
        field: "name",
        sortable: true,
        filter: true,
      },
      {
        headerName: prettifyHeader("email"),
        field: "email",
        sortable: true,
        filter: true,
      },
      {
        headerName: prettifyHeader("roll_no"),
        field: "roll_no",
        sortable: true,
        filter: true,
      },
      {
        headerName: prettifyHeader("batch"),
        field: "batch_name",
        sortable: true,
        filter: true,
      },
    ];
  }, []);

  const defaultColDef: ColDef = {
    flex: 1,
    minWidth: 140,
    resizable: true,
    editable: false,
  };

  if (studentQuery.isLoading) return <div>Loading...</div>;
  if (studentQuery.isError) return <div>Error loading data</div>;

  return (
    <div className="ag-theme-alpine" style={{ height: 520, width: "100%", marginTop: "16px" }}>
      <AgGridReact
        rowData={rowData}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        pagination
        paginationPageSize={10}
        animateRows
      />
    </div>
  );
};

export default ReadStudent;
